import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { userAccSocket } from '@entities/account'
import { useUserAccStore } from './userAccStore'

const USER_ACC_STALE_MS = 10_000
const USER_ACC_WATCHDOG_CHECK_MS = 2_000
const USER_ACC_RECONNECT_COOLDOWN_MS = 30_000

interface UserAccSocketOptions {
  watchdogEnabled?: boolean
}

export const useUserAccSocket = (
  enabled: boolean,
  { watchdogEnabled = false }: UserAccSocketOptions = {},
) => {
  const queryClient = useQueryClient()
  const watchdogEnabledRef = useRef(watchdogEnabled)

  useEffect(() => {
    watchdogEnabledRef.current = watchdogEnabled
  }, [watchdogEnabled])

  useEffect(() => {
    if (!enabled) return

    let lastMessageAt = Date.now()
    let lastReconnectAt = 0

    const refetchOrderState = () => {
      queryClient.invalidateQueries({ queryKey: ['order', 'positions'] })
      queryClient.invalidateQueries({ queryKey: ['order', 'openOrders'] })
    }

    const reconnectIfStale = () => {
      if (!watchdogEnabledRef.current) return
      if (document.visibilityState !== 'visible') return

      const now = Date.now()
      if (now - lastMessageAt < USER_ACC_STALE_MS) return
      if (now - lastReconnectAt < USER_ACC_RECONNECT_COOLDOWN_MS) return

      lastReconnectAt = now
      lastMessageAt = now
      userAccSocket.disconnect()
      userAccSocket.connect()
      refetchOrderState()
    }

    const remove = userAccSocket.addListener(msg => {
      lastMessageAt = Date.now()

      if (msg.cmd === 9001) {
        if (msg.rc !== 0) {
          userAccSocket.disconnect()
          useUserAccStore.getState().reset()
        }
        return
      }
      if (msg.cmd === 2101) {
        useUserAccStore.getState().applyWsAssets(msg)
        return
      }
      if (msg.cmd === 2102) {
        useUserAccStore.getState().applyWsPositions(msg)
        return
      }
      if (msg.cmd === 2103) {
        useUserAccStore.getState().applyWsOpenOrders(msg)
        return
      }
      if (msg.cmd === 2104) {
        useUserAccStore.getState().appendTpEvents(msg.datas)
        return
      }
      if (msg.cmd === 2105) {
        useUserAccStore.getState().setTpStatus(msg.datas)
        return
      }
      if (msg.cmd === 2110) {
        // 알람 새로 도착 → 캐시에 prepend
        const newAlarm: Alarm = {
          alarmId: msg.sendTime,
          alarmType: msg.alarmType ?? '',
          level: msg.level,
          accId: '',
          brokerCode: '',
          message: msg.message,
          payloadJson: null,
          readStatus: 0,
          readAt: '',
          createdAt: new Date(msg.sendTime).toISOString().replace('T', ' ').replace('Z', ''),
          updatedAt: '',
        }
        queryClient.setQueryData<AlarmListResponse>(['alarm', 'list'], prev => {
          if (!prev) {
            return { code: 200, message: 'ok', alarms: [newAlarm] }
          }
          return { ...prev, alarms: [newAlarm, ...prev.alarms] }
        })
        // staleTime 안에 있어도 강제 refetch 트리거
        queryClient.invalidateQueries({ queryKey: ['alarm', 'list'] })
        return
      }
      if (msg.cmd === 4000) {
        // 봇 상태 변경 → REST 봇 리스트 캐시의 status 패치
        // 매칭 키: symbol + side (소켓 side는 0 BUY / 1 SELL)
        const patchStatus = (item: BotListItem): BotListItem =>
          item.symbol === msg.symbol &&
          (item.positionSide === 'SELL' ? 1 : 0) === msg.side
            ? { ...item, status: msg.status }
            : item

        // 기존 봇 쿼리가 있을 때만 패치한다. setQueryData는 없는 캐시도 만들 수 있어 피한다.
        queryClient.setQueriesData<BotListResponse>(
          { queryKey: ['bot', 'list'] },
          prev => (prev ? { ...prev, botList: prev.botList.map(patchStatus) } : prev),
        )
        queryClient.setQueriesData<BotListResponse>({ queryKey: ['bot', 'all'] }, prev =>
          prev ? { ...prev, botList: prev.botList.map(patchStatus) } : prev,
        )
        return
      }
    })

    const watchdogTimer = window.setInterval(reconnectIfStale, USER_ACC_WATCHDOG_CHECK_MS)
    const handleResume = () => {
      reconnectIfStale()
    }

    document.addEventListener('visibilitychange', handleResume)
    window.addEventListener('focus', handleResume)
    window.addEventListener('online', handleResume)
    userAccSocket.connect()

    return () => {
      window.clearInterval(watchdogTimer)
      document.removeEventListener('visibilitychange', handleResume)
      window.removeEventListener('focus', handleResume)
      window.removeEventListener('online', handleResume)
      remove()
      userAccSocket.disconnect()
      useUserAccStore.getState().reset()
    }
  }, [enabled, queryClient])
}
